import React, { useContext, useEffect, useState } from 'react';
import { merge } from 'react-merge';
import { useMediaQuery } from 'react-responsive';
import { StyleContext } from '../../providers/StyleContextProvider';
import { 
    PasswordStrengthLabel, 
    PasswordValidationConfig, 
    validatePasswordRules, 
    getPasswordStrengthLabel 
} from '../../helpers/Common';

interface PasswordValidationProps {
    password: string;
    touched?: boolean;
    style?: any;
    showStrength?: boolean;
    focused?: boolean;
    config?: PasswordValidationConfig;
}

const CheckIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
    <svg style={style} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" clipRule="evenodd" />
    </svg>
);

const BulletIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
    <svg style={style} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <circle cx="10" cy="10" r="3" />
    </svg>
);

const STRENGTH_SEGMENTS: Record<PasswordStrengthLabel, number> = {
    Weak: 1, Fair: 2, Good: 3, Strong: 4
};

interface RuleItemProps {
    met: boolean;
    label: string;
    itemStyle: any;
    iconStyle: any; 
}

const RuleItem: React.FC<RuleItemProps> = ({ met, label, itemStyle, iconStyle }) => (
    <li style={{ ...itemStyle, ...(met ? itemStyle.met : itemStyle.unmet) }}>
        {met
            ? <CheckIcon style={{ ...iconStyle, color: itemStyle.met?.color }} />
            : <BulletIcon style={iconStyle} />
        }
        <span>{label}</span>
    </li>
);

export const PasswordValidation: React.FC<PasswordValidationProps> = ({ password, touched = false, style, showStrength = false, focused = true, config }) => {
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle('passwordValidation'));
    const isMobile = useMediaQuery({ query: '(max-width: 786px)' });
    const rules = validatePasswordRules(password, config);
    const strengthLabel = getPasswordStrengthLabel(rules);
    const isValid = rules.isValid;
    const hasValue = password.length > 0;
    const showError = hasValue && !isValid && touched;
    const showPanel = hasValue && focused;
    const { hasLower, hasUpper, hasNumber, hasSpecial } = rules;
    const categoriesRowMet = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length >= rules.config.minCategories;
    const activeSegments = STRENGTH_SEGMENTS[strengthLabel];
    const badgeStyle = { ...styles.strengthBadge, ...styles.strengthBadge?.[strengthLabel] };

    useEffect(() => {
        let merged = merge(styles, style);
        setStyles(merged);
    }, [isMobile]);

    if (!showPanel && !showError) return null;

    return (
        <div style={styles.root}>
            {showPanel && (
                <div style={{ ...styles.panel, ...(isValid && styles.panel?.valid) }} aria-live="polite" aria-atomic="false" role="status" >
                    <div style={styles.panelHeader}>
                        <span style={styles.panelTitle}>Your password must contain:</span>
                        {showStrength && (
                            <span style={badgeStyle}>{strengthLabel}</span>
                        )}
                    </div>
                    {showStrength && (
                        <div style={styles.meterTrack} aria-hidden="true">
                            {[1, 2, 3, 4].map(seg => (
                                <div key={seg} style={{ ...styles.meterSegment, ...(seg <= activeSegments && styles.meterSegment?.[strengthLabel]) }} />
                            ))}
                        </div>
                    )}

                    <ul style={styles.rulesList} aria-label="Password requirements">
                        <RuleItem met={rules.hasMinChars} label={`At least ${rules.config.minLength} characters`} itemStyle={styles.ruleItem} iconStyle={styles.ruleIcon} />
                        <li style={{ ...styles.ruleItem, ...(categoriesRowMet ? styles.ruleItem?.met : styles.ruleItem?.unmet) }}>
                            {categoriesRowMet
                                ? <CheckIcon style={{ ...styles.ruleIcon, color: styles.ruleItem?.met?.color }} />
                                : <BulletIcon style={styles.ruleIcon} />
                            }
                            <span>At least {rules.config.minCategories} of the following:</span>
                        </li>

                        <ul style={styles.subList} role="list" aria-label="Character categories">
                            <RuleItem met={hasLower} label="Lower case letters (a-z)" itemStyle={styles.subItem} iconStyle={styles.subIcon} />
                            <RuleItem met={hasUpper} label="Upper case letters (A-Z)" itemStyle={styles.subItem} iconStyle={styles.subIcon} />
                            <RuleItem met={hasNumber} label="Numbers (0-9)" itemStyle={styles.subItem} iconStyle={styles.subIcon} />
                            <RuleItem met={hasSpecial} label="Special characters (e.g. !@#$%^&*)" itemStyle={styles.subItem} iconStyle={styles.subIcon} />
                        </ul>
                    </ul>
                </div>
            )}
        </div>
    );
};